import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { NotificationLine } from "./NotificationLine";
import { localDateTime } from "../../lib/datetime";

// عنصر واحد في السجل: غير المقروء مميّز بنقطة وخلفية خفيفة.
// إشعارات المكافآت لا تحمل زر «صح» حتى لا يُفهم أنه تأكيد استلام — بل تفتح صفحة مكافآتي.
const REQUEST_KINDS = ["leave_request_new", "resume_request_new", "advance_request_new", "resignation_request_new", "time_off_created"];

export const NotificationItem = ({ item, onRead, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const unread = !item.read;
  const ar = (i18n.language || "ar").startsWith("ar");
  const isBroadcast = item.type === "platform_broadcast" || item.type === "company_notice";
  const pick = (a, b) => (ar ? a || b : b || a);
  const isBonus = item.type.startsWith("bonus_");
  const isBill = item.type === "bill_awaiting_signature";
  const isRequest = REQUEST_KINDS.includes(item.type);
  const clickable = isBonus || isBill || isRequest;

  const open = () => {
    if (!clickable) return;
    if (unread) onRead(item.id);
    if (onClose) onClose();
    if (isBill) navigate(`/inbox/bill/${item.data?.bill_id || ""}`);
    else if (isRequest) navigate("/requests");
    else navigate(item.data?.link || "/my-bonuses");
  };

  return (
    <li
      className={`flex items-start gap-3 rounded-2xl px-3 py-3 ${unread ? "bg-primary/5" : ""} ${
        clickable ? "cursor-pointer" : ""
      }`}
      onClick={open}
      data-testid={`notification-${item.id}`}
    >
      <span
        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${unread ? "bg-primary" : "bg-transparent"}`}
        data-testid={unread ? `notification-unread-dot-${item.id}` : undefined}
      />
      <div className="flex-1">
        <p className={`text-sm ${unread ? "font-black" : "font-bold text-muted-foreground"}`}>
          {isBroadcast
            ? (pick(item.data?.title_ar, item.data?.title_en) || item.data?.title || t("notifications.broadcastTitle"))
            : t(`notifications.type.${item.type}`, { defaultValue: ar ? "لديك إشعار جديد" : "You have a new notification" })}
        </p>
        {isBroadcast ? (
          <p className="text-xs text-muted-foreground">{pick(item.data?.body_ar, item.data?.body_en)}</p>
        ) : (
          <NotificationLine item={item} />
        )}
        {isBonus ? (
          <p className="mt-1 text-[11px] font-black text-primary" data-testid={`notification-open-${item.id}`}>
            {t("notifications.openBonus")}
          </p>
        ) : null}
        {isBill ? (
          <p className="mt-1 text-[11px] font-black text-emerald-600" data-testid={`notification-open-${item.id}`}>
            {t("notifications.openBillSign")}
          </p>
        ) : null}
        {isRequest ? (
          <p className="mt-1 text-[11px] font-black text-primary" data-testid={`notification-open-${item.id}`}>
            {t("notifications.openApprovals")}
          </p>
        ) : null}
        <p className="mt-0.5 font-mono text-[11px] text-muted-foreground" dir="ltr">
          {localDateTime(item.at)}
        </p>
      </div>
      {unread && !clickable ? (
        <button
          type="button"
          onClick={() => onRead(item.id)}
          data-testid={`notification-read-${item.id}`}
          aria-label={t("notifications.markRead")}
          className="rounded-xl p-2 shadow-neu-sm transition-[box-shadow] hover:shadow-neu-in"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
      ) : null}
    </li>
  );
};
